/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { isEmptyObject } from '../components/OriginDestinationSelector/utils';
import { UpSell_Type, CABIN_CLASS, defaultSort } from '../config/constants';
import type { SearchFlightFilters } from '../hooks/flights';
import type {
  FunnelControlResponse,
  SearchAggregatorFilters,
  SSODetails,
  UserContext,
} from '../types/FlightSearch.types';
import { TripType } from '../types/FlightSearch.types';
import {
  formatDateDDMMYYYY,
  getStringQueryParam,
  normalizedFareClass,
} from './utils';

// Pure util, no hook access - callers pass their own logError-backed
// onValidationError so this stays usable outside React while still letting
// each hook track which field was missing.
const assertDefined = <T>(
  v: T | undefined | null,
  msg: string,
  onValidationError?: (msg: string) => void,
): T => {
  if (v === undefined || v === null) {
    onValidationError?.(msg);
    throw new Error(msg);
  }
  return v;
};

export type SearchAggregatorQueryParams = Record<
  string,
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean>
>;

export const buildAggregatorFilterParams = (args: {
  filters: SearchFlightFilters;
  funnel: FunnelControlResponse;
  consumerId?: string;
  onValidationError?: (msg: string) => void;
}): SearchAggregatorFilters => {
  const { filters, funnel, consumerId, onValidationError } = args;
  const selectedDepartFlight =
    filters.tripType !== TripType.ONE_WAY &&
    !isEmptyObject(filters.selectedDepartFlight)
      ? filters.selectedDepartFlight
      : undefined;

  const originObj = assertDefined(
    filters.originObj,
    'Origin missing',
    onValidationError,
  );
  const destinationObj = assertDefined(
    filters.destinationObj,
    'Destination missing',
    onValidationError,
  );
  const departDate = assertDefined(
    filters.departDate,
    'Depart date missing',
    onValidationError,
  );

  const departStation = assertDefined(
    originObj.IsCity ? originObj.CityCode : originObj.StationCode,
    'Depart station missing',
    onValidationError,
  );

  const arrivalStation = assertDefined(
    destinationObj.IsCity
      ? destinationObj.CityCode
      : destinationObj.StationCode,
    'Arrival station missing',
    onValidationError,
  );

  return {
    page: filters.page ?? 1,
    sort: filters.sort,

    airlineProfile: funnel.airlineProfile ?? 'all',
    type: funnel.type,

    fareClass: filters.cabinClass,

    enableSnapUpsell: funnel.enableSnapUpsell,
    enablePremiumFlatbedUpsell: funnel.enablePremiumFlatbedUpsell,
    enableUnifiedCheckout: funnel.enableUCE,

    isOriginCity: Boolean(originObj.IsCity),
    isDestinationCity: Boolean(destinationObj.IsCity),

    departStation,
    arrivalStation,
    departDate: formatDateDDMMYYYY(departDate),
    returnDate: filters.returnDate
      ? formatDateDDMMYYYY(filters.returnDate)
      : null,

    adult: filters.adultCount ?? 1,
    child: filters.childCount ?? 0,
    infant: filters.infantCount ?? 0,

    promocode: filters.promoCode ?? null,
    consumerId,
    selectedDepartFlight,
  };
};

export const buildAggregatorQueryParams = (args: {
  filterParams: SearchAggregatorFilters;
  funnel: FunnelControlResponse;
  showSoldOutFlights?: boolean;
  isDistress?: boolean;
}): SearchAggregatorQueryParams => {
  const {
    filterParams,
    funnel,
    showSoldOutFlights = false,
    isDistress = false,
  } = args;
  const airlineProfileFromParams =
    getStringQueryParam('airlineProfile') ?? undefined;
  const providerFromParams = getStringQueryParam('providers') ?? undefined;
  const taIDFromParams = getStringQueryParam('taIDs') ?? undefined;

  const includeList = [
    'searchResults',
    'currency',
    'content',
    'featureFlags',
    'locale',
    'vouchers',
    funnel.enableSnapUpsell ? UpSell_Type.snap : null,
    funnel.enableFlightBusUpsell ? UpSell_Type.flightBus : null,
    funnel.enablePremiumFlatbedUpsell ? UpSell_Type.flatbed : null,
    filterParams.fareClass === CABIN_CLASS.ECONOMY &&
    funnel.enablePremiumFlatbedUpsell
      ? UpSell_Type.premiumFlatBed
      : null,
  ].filter(Boolean) as string[];

  return {
    page: filterParams.page ?? 1,
    include_list: includeList,
    airlineProfile: isDistress
      ? 'd'
      : (airlineProfileFromParams ?? funnel.airlineProfile ?? 'all'),
    type: funnel.type,
    isPromoMessagesByCode: true,
    isOriginCity: Boolean(filterParams.isOriginCity),
    isDestinationCity: Boolean(filterParams.isDestinationCity),
    uce: Boolean(funnel.enableUCE),
    ...(showSoldOutFlights ? { showSoldOutFlights: true } : {}),
    ...(providerFromParams ? { providers: providerFromParams } : {}),
    ...(taIDFromParams ? { taIDs: taIDFromParams } : {}),
  };
};

export const buildAggregatorBody = (args: {
  filters: SearchFlightFilters;
  filterParams: SearchAggregatorFilters;
  userContext: UserContext;
  ssoDetails: SSODetails;
}) => {
  const { filters, filterParams, userContext, ssoDetails } = args;
  const selectedDepartFlight = filterParams.selectedDepartFlight;
  const isReturnJourneySelection = !!selectedDepartFlight;

  return {
    consumerId: filterParams.consumerId,
    flightJourney: {
      journeyType: filters.tripType === TripType.ONE_WAY ? 'O' : 'R',
      journeyDetails: [
        {
          origin: filterParams.departStation,
          destination: filterParams.arrivalStation,
          departDate: filterParams.departDate,
          returnDate:
            filters.tripType === TripType.ONE_WAY
              ? null
              : (filterParams.returnDate ?? null),
        },
      ],
      passengers: {
        adult: filterParams.adult,
        child: filterParams.child,
        infant: filterParams.infant,
      },
    },
    searchContext: {
      promocode: filterParams.promocode,
      sort: filterParams.sort ?? defaultSort,
      filters: {
        cabin: {
          cabinClass: normalizedFareClass(filters.cabinClass),
          applyMixedClasses: filters.applyMixedClasses,
        },
        stops: {
          stopType: filters.stopType,
          allowOvernight: filters.allowOvernight,
        },
        duration: {
          maxTravelTimeInHrs: filters.maxTravelTimeInHrs,
          maxStopoverTimeInHrs: filters.maxStopoverTimeInHrs,
          minStopoverTimeInHrs: filters.minStopoverTimeInHrs,
        },
        carriers: {
          allowAllCarriers: filters.allowAllCarriers,
          onlyAllowedCarriers: filters.onlyAllowedCarriers,
          excludedCarriers: filters.excludedCarriers,
        },
        departAirports: {
          allowAllAirports: filters.allowAllAirports,
          allowedDepartAirports: filters.allowedDepartAirports,
        },
        returnAirports: {
          allowAllAirports: filters.allowAllReturnAirports,
          allowedReturnAirports: filters.allowedReturnAirports,
        },
        journey: isReturnJourneySelection ? 'R' : 'O',
      },
    },
    userContext,
    ssoDetails,
    selectedDepartFlight: selectedDepartFlight
      ? {
          designator: selectedDepartFlight?.flightDetails?.designator,
          airlineProfile: selectedDepartFlight?.airlineProfile,
          tripId: selectedDepartFlight?.tripId,
          ...(filters.departPointsPlusCashEnabled && {
            redemptionPoints: selectedDepartFlight.redemptionPoints,
          }),
        }
      : null,
  };
};
